import React, { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import {
  CircularProgress,
  IconButton,
  TextField,
  Box,
  Typography,
  Chip,
  Alert,
  Snackbar,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { FaEdit } from 'react-icons/fa';

// Components
import CustomButton from "../../components/common/CustomButton/CustomButton.tsx";
import { Column } from "../../components/common/CustomDataTable/CustomDataTable.tsx";
import { CustomTabPanel, SubHeaderTitle } from "../../components/common/index";
import { CommonData, } from "../PageImports.ts";
import ErrorPage from "../Error/ErrorPage.tsx";
import Loader from "../../utils/Loader.tsx";

// Utilities
import { axiosFunction } from '../../components/commonFunctions/axiosFunction.ts';
import { SwalFunction } from '../../components/commonFunctions/SwalFunction.ts';
import { v2ReactURL } from '../../utils/constant.ts';
import { capitalizeFirstChar, CheckedValue, debounce, ErrorState, Pagination } from '../Contact/MainDataTable.tsx';
import { useNavigate } from "react-router-dom";

const {
  Wrapper,
  WrapperSubHeader,
  WrapperContent,
  WrapperCard,
  Row,
  CustomDataTable,
} = CommonData;

// const { toastMessage } = CommonFunctions;

interface DeliverySheetProps {
  navUrl: string;
}

interface DeliverySheetData {
  deliveryAssignId: number;
  status: string;
  contactId: number;
  createdBy: string;
  deliveryDate: string;
  driverName: string;
  delivereyAssignNo: string;
  vehicleNo: string;
  delvierySlotName: string;
}

interface DeliverySheetFormData {
  deliverySheetId: number;
  deliverySheetName: string;
  deliverySheetType: string;
  startTime: string;
  endTime: string;
  cutOffHours: number;
  maximumOrders: number;
  calculatedCutOffTime: string;
  isActive: number;
}

interface UserPermissions {
  canView: boolean;
  canInsert: boolean;
  canEdit: boolean;
  canDelete: boolean;
  loading: boolean;
  error: string | null;
}

const DeliverySheet: React.FC<DeliverySheetProps> = ({ navUrl }) => {
  const tabView = navUrl;
  
  // Permission state
  const [userPermissions, setUserPermissions] = useState<UserPermissions>({
    canView: false,
    canInsert: false,
    canEdit: false,
    canDelete: false,
    loading: true,
    error: null
  });

  const [selectedDeliverySheet, setSelectedDeliverySheet] = useState<DeliverySheetData | null>(null);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [openCreateModal, setOpenCreateModal] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [rows, setRows] = useState<DeliverySheetData[]>([]);
  const [loadingData, setLoadingData] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [deleting, setDeleting] = useState<boolean>(false);
  const [approving, setApproving] = useState<boolean>(false);
  const [pagination, setPagination] = useState<Pagination>({
    page: 0,
    pages: 0,
    perpage: 0,
    total: 0,
  });
  const [pageOption, setPageOption] = useState<string>('15');
  const [searchValue, setSearchValue] = useState<string>('');
  const [error, setError] = useState<ErrorState | null>(null);
  const [checkedValue, setCheckedValue] = useState<CheckedValue>({});
  const [openFilter, setOpenFilter] = useState<string | null>(null);
  const [searchInputValue, setSearchInputValue] = useState<string>('');
  const [deliverySheetType, setDeliverySheetType] = useState<string>('general');

  // Form state
  const [formData, setFormData] = useState<DeliverySheetFormData>({
    deliverySheetId: 0,
    deliverySheetName: '',
    deliverySheetType: 'general',
    startTime: '',
    endTime: '',
    cutOffHours: 0,
    maximumOrders: 0,
    calculatedCutOffTime: '',
    isActive: 1,
  });

  const [showValidation, setShowValidation] = useState<boolean>(false);
  const navigate = useNavigate();

  // Snackbar state for toast messages
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'warning' | 'info',
    vertical: 'bottom' as 'top' | 'bottom',
    horizontal: 'right' as 'left' | 'center' | 'right'
  });

  // Show snackbar function
  const showSnackbar = (message: string, severity: 'success' | 'error' | 'warning' | 'info' = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity,
      vertical: 'bottom',
      horizontal: 'right'
    });
  };

  const handleSnackbarClose = (_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Override toastMessage function to use our custom snackbar
  const customToastMessage = (message: string, type: 'success' | 'error' | 'warning' | 'info' = 'success') => {
    showSnackbar(message, type);
  };

  // Fetch user permissions - DIRECT API CALL
  const fetchUserPermissions = async (): Promise<boolean> => {
    try {
      setUserPermissions(prev => ({ ...prev, loading: true, error: null }));
      
      const response = await axiosFunction(
        `${v2ReactURL}/native/getuserrolepermission`,
        'GET',
        false
      );

      console.log("Permission API response:", response.data.response.reportListResponse);

      if (response.data?.status && response.data.response?.reportListResponse) {
        const reportList = response.data.response.reportListResponse;
        
        // Check if reportList is an array and find delivery permissions
        if (Array.isArray(reportList)) {
          // Find the delivery type permissions by mapping through the array
          const deliveryPermissions = reportList.find((item: any) => 
            item.type && item.type.toLowerCase() === 'delivery'
          );
          
          console.log("Found delivery permissions:", deliveryPermissions);
          
          if (deliveryPermissions && deliveryPermissions.permissionids) {
            let permissionArray: string[] = [];
            
            if (typeof deliveryPermissions.permissionids === 'string') {
              // Split comma-separated string into array
              permissionArray = deliveryPermissions.permissionids.split(',').map((id: string) => id.trim());
            } else if (Array.isArray(deliveryPermissions.permissionids)) {
              // If it's already an array, use it directly
              permissionArray = deliveryPermissions.permissionids.map((id: any) => id.toString());
            }
            
            console.log("Delivery permissions array:", permissionArray);
            
            const canView = permissionArray.includes('2');
            
            setUserPermissions({
              loading: false,
              canView: canView,
              canInsert: permissionArray.includes('1'),
              canEdit: permissionArray.includes('3'),
              canDelete: permissionArray.includes('4'),
              error: null
            });
            
            return canView;
          } else {
            // No delivery permissions found in the array
            console.warn('No delivery permissions found in reportListResponse array');
            setUserPermissions({
              loading: false,
              canView: false,
              canInsert: false,
              canEdit: false,
              canDelete: false,
              error: 'No delivery permissions found in the response'
            });
            return false;
          }
        } else {
          // reportListResponse is not an array
          console.error('reportListResponse is not an array:', reportList);
          setUserPermissions({
            loading: false,
            canView: false,
            canInsert: false,
            canEdit: false,
            canDelete: false,
            error: 'Invalid response format: reportListResponse is not an array'
          });
          return false;
        }
      } else {
        console.warn('Invalid API response structure');
        setUserPermissions({
          loading: false,
          canView: false,
          canInsert: false,
          canEdit: false,
          canDelete: false,
          error: response.data?.message || 'Failed to load permissions - invalid response structure'
        });
        return false;
      }
    } catch (error: any) {
      console.error('Error fetching permissions:', error);
      setUserPermissions({
        loading: false,
        canView: false,
        canInsert: false,
        canEdit: false,
        canDelete: false,
        error: 'Error fetching permissions from server'
      });
      return false;
    }
  };

  const fetchData = async (
    searchValue: string,
    startIndex: number,
    pageLength: number,
  ): Promise<void> => {
    setLoadingData(true);
    const payloadData = {
      searchValue: searchValue,
      length: pageLength,
      start: startIndex,
      draw: 1,
    };

    try {
      const res = await axiosFunction(
        `${v2ReactURL}/native/delivery/deliverySheet/datatable`,
        "POST",
        payloadData
      );
      
      const responseData = res?.response?.data || res?.data || res;
      
      console.log("Delivery Sheet API Response:", responseData);

      if (responseData && responseData.response) {
        const deliverySheetData = responseData.response;
        
        if (deliverySheetData.data && Array.isArray(deliverySheetData.data)) {
          const deliverySheetList = deliverySheetData.data.map((col: DeliverySheetData, index: number) => ({
            ...col,
            index: index + 1,
            checked: false,
          }));
          
          setPagination(deliverySheetData.dataTableMetaDTO || {
            page: 0,
            pages: 1,
            perpage: pageLength,
            total: deliverySheetData.data.length
          });
          setRows(deliverySheetList);
        }
      } else if (responseData && Array.isArray(responseData.data)) {
        const deliverySheetList = responseData.data.map((col: DeliverySheetData, index: number) => ({
          ...col,
          index: index + 1,
          checked: false,
        }));
        
        setPagination(responseData.dataTableMetaDTO || {
          page: 0,
          pages: 1,
          perpage: pageLength,
          total: responseData.data.length
        });
        setRows(deliverySheetList);
      } else {
        throw new Error("Invalid API response format");
      }
      
      setIsLoading(false);
      setLoadingData(false);
      
    } catch (error: any) {
      console.error("Error fetching delivery sheet data:", error);
      handleApiError(error);
    }
  };

  const handleApiError = (error: any) => {
    if (error.code === "ERR_NETWORK" || error.message === "network error") {
      setError({
        code: "502",
        message: "Hang tight! Our checkout lanes are undergoing maintenance. We'll be back to serving you real soon!",
      });
    } else if (error.response?.status === 400) {
      setError({
        code: "400",
        message: "Oops, your request got lost in translation! Maybe try again with simpler instructions?",
      });
    } else if (error.response?.status === 404) {
      setError({
        code: "404",
        message: "Oopsie! Looks like this page went on vacation. Maybe try a different path?",
      });
    } else if (error.response?.status === 500) {
      setError({
        code: "500",
        message: "Uh-oh, our server's taking a coffee break. It'll be back to ringing up sales soon!",
      });
    } else {
      setError({
        code: "500",
        message: "An unexpected error occurred while fetching delivery sheet data.",
      });
    }
    
    customToastMessage("Error fetching delivery sheet details", 'error');
    setLoadingData(false);
    setIsLoading(false);
  };

  const handleSaveDeliverySheet = async () => {
    // Set validation state to true to show all errors
    setShowValidation(true);

    // Basic validations
    if (!formData.deliverySheetName.trim()) {
      customToastMessage("Delivery sheet name is required", 'error');
      return;
    }

    if (!formData.startTime) {
      customToastMessage("Start time is required", 'error');
      return;
    }

    if (!formData.endTime) {
      customToastMessage("End time is required", 'error');
      return;
    }

    if (formData.deliverySheetType !== 'general') {
      if (formData.cutOffHours < 0) {
        customToastMessage("Cut-off hours cannot be negative", 'error');
        return;
      }
      if (formData.maximumOrders <= 0) {
        customToastMessage("Maximum orders must be greater than 0", 'error');
        return;
      }
    }

    // If all validations pass, proceed with saving
    setSaving(true);
    try {
      const payload = {
        deliverySheetId: formData.deliverySheetId,
        deliverySheetName: formData.deliverySheetName.trim(),
        deliverySheetType: formData.deliverySheetType,
        startTime: formData.startTime,
        endTime: formData.endTime,
        cutOffHours: formData.cutOffHours,
        maximumOrders: formData.maximumOrders,
        calculatedCutOffTime: formData.calculatedCutOffTime,
        isActive: formData.isActive,
      };

      const res = await axiosFunction(
        `${v2ReactURL}/native/saveDeliverySheet`,
        "POST",
        payload
      );

      if (res.status === 200 || res.status === 201) {
        customToastMessage(`Delivery sheet ${formData.deliverySheetId ? 'updated' : 'created'} successfully!`, 'success');
        setOpenCreateModal(false);
        resetForm();
        handleFetchData();
      } else {
        customToastMessage(res.data?.message || `Failed to ${formData.deliverySheetId ? 'update' : 'create'} delivery sheet`, 'error');
      }
    } catch (error: any) {
      console.error("Error saving delivery sheet:", error);
      customToastMessage(`Error ${formData.deliverySheetId ? 'updating' : 'creating'} delivery sheet`, 'error');
    } finally {
      setSaving(false);
    }
  };

  // Delete delivery sheet function
  const handleDeleteDeliverySheet = async (deliveryAssignId: number) => {
    // if (!window.confirm('Are you sure you want to delete this delivery sheet?')) {
    //   return;
    // }

    setDeleting(true);
    try {
      const res = await axiosFunction(
        `${v2ReactURL}/native/delivery/deliverySheet/${deliveryAssignId}/delete`,
        "POST",
        {},
        false
      );

      if (res.status === 200 || res.status === 201) {
        customToastMessage('Delivery sheet deleted successfully!', 'success');
        handleFetchData(); // Refresh the data
      } else {
        customToastMessage(res.data?.message || 'Failed to delete delivery sheet', 'error');
      }
    } catch (error: any) {
      console.error("Error deleting delivery sheet:", error);
      customToastMessage('Error deleting delivery sheet', 'error');
    } finally {
      setDeleting(false);
    }
  };

  // Approve delivery sheet function
  const handleApproveDeliverySheet = async (deliverySheetId: number) => {
    // Show confirmation modal
    const result = await SwalFunction(
      "Are You Sure?",
      "Do you want to approve this delivery sheet?",
      "Yes, Approve It!",
      { icon: "question" }
    );

    if (!result.isConfirmed) {
      return;
    }

    // setApproving(true);
    try {
      const res = await axiosFunction(
        `${v2ReactURL}/native/delivery/deliverySheet/approve?deliverysheetId=${deliverySheetId}`,
        "POST",
        {},
        false
      );

      if (res.status === 200 || res.status === 201) {
        customToastMessage('Delivery sheet approved successfully!', 'success');
        handleFetchData(); // Refresh the data table
      } else {
        customToastMessage(res.data?.message || 'Failed to approve delivery sheet', 'error');
      }
    } catch (error: any) {
      console.error("Error approving delivery sheet:", error);
      customToastMessage('Error approving delivery sheet', 'error');
    } finally {
      setApproving(false);
    }
  };

  const handleEditDeliverySheet = (row: DeliverySheetData) => {
    console.log("Editing delivery sheet:", row.deliveryAssignId);
    localStorage.setItem('IsEdit', "True");
    localStorage.setItem('IsView', "False");
    localStorage.setItem('deliverySheetId', row.deliveryAssignId.toString());
    setSelectedDeliverySheet(row);
    navigate("/deliverySheet/create")
    // Since the form data structure is different from the API response,
    // you might need to adjust this mapping based on your actual form requirements
    setFormData({
      deliverySheetId: row.deliveryAssignId, // Map deliveryAssignId to deliverySheetId
      deliverySheetName: row.driverName || '', // Using driverName as sheet name
      deliverySheetType: 'general',
      startTime: '',
      endTime: '',
      cutOffHours: 0,
      maximumOrders: 0,
      calculatedCutOffTime: '',
      isActive: 1,
    });
    setShowValidation(false);
    setOpenCreateModal(true);
  };

  const resetForm = () => {
    setFormData({
      deliverySheetId: 0,
      deliverySheetName: '',
      deliverySheetType: deliverySheetType,
      startTime: '',
      endTime: '',
      cutOffHours: 0,
      maximumOrders: 0,
      calculatedCutOffTime: '',
      isActive: 1,
    });
    setSelectedDeliverySheet(null);
    setShowValidation(false);
  };

  const calculateCutOffTime = (cutOffHours: number, startTime: string) => {
    if (!startTime) return;

    const parts = startTime.split(':');
    const hours = Number(parts[0] ?? 0);
    const minutes = Number(parts[1] ?? 0);
    const startDate = new Date();
    startDate.setHours(hours, minutes, 0, 0);

    const cutOffTime = new Date(startDate.getTime() - (cutOffHours * 60 * 60 * 1000));

    const cutOffHoursStr = cutOffTime.getHours().toString().padStart(2, '0');
    const cutOffMinutesStr = cutOffTime.getMinutes().toString().padStart(2, '0');

    setFormData(prev => ({
      ...prev,
      calculatedCutOffTime: `${cutOffHoursStr}:${cutOffMinutesStr}`
    }));
  };

  const handleFetchData = async (): Promise<void> => {
    await fetchData(
      searchValue,
      pagination.page * parseInt(pageOption),
      parseInt(pageOption),
    );
  };

  useEffect(() => {
    // Initialize data with proper permission checking
    const initializeData = async () => {
      const hasViewPermission = await fetchUserPermissions();
      
      if (hasViewPermission) {
        await handleFetchData();
      }
    };

    initializeData();
  }, []);

  useEffect(() => {
    // Update form data when deliverySheetType changes
    if (!formData.deliverySheetId) { // Only update for new sheets
      setFormData(prev => ({
        ...prev,
        deliverySheetType: deliverySheetType
      }));
    }
  }, [deliverySheetType]);

  const handleChangePageOption = (e: { target: { value: any } }) => {
    const selectedValue = e.target.value;
    setPageOption(parseInt(selectedValue).toString());
    fetchData(
      searchValue,
      0,
      parseInt(selectedValue),
    );
  };

  const handlePageChange = (_: React.ChangeEvent<unknown>, page: number) => {
    fetchData(
      searchValue,
      (page - 1) * parseInt(pageOption),
      parseInt(pageOption),
    );
  };

  const handleSearchData = useCallback(
    debounce((searchValue: string) => {
      fetchData(
        searchValue,
        0,
        parseInt(pageOption),
      );
    }, 500),
    [pageOption]
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);
    handleSearchData(value);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const getStatusColor = (status: string) => {
    const statusLower = status?.toLowerCase();
    if (statusLower === 'open') return 'error'; // RED
    if (statusLower === 'approval') return 'success'; // GREEN
    return 'default';
  };

  const columnsDataTableDeliverySheet: Column[] = [
    {
      field: "srNo",
      headerName: "Sr. No",
      width: 60,
      defaultChecked: true,
      renderCell: (params: any) => (
        <Typography variant="body2" color="textSecondary">
          {pagination.page * pagination.perpage + params.row.index}
        </Typography>
      ),
    },
     
    {
      field: "deliveryAssignId",
      headerName: "Delivery Sheet No",
      width: 120,
      defaultChecked: true,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="500"  onClick={() => handleOpenModal(params.row)} style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}>
          DEL{params.row.deliveryAssignId}
        </Typography>
      ),
    },
    {
      field: "driverName",
      headerName: "Driver Name",
      width: 120,
      defaultChecked: true,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="500">
          {params.row.driverName}
        </Typography>
      ),
    },
    {
      field: "vehicleNo",
      headerName: "Vehicle No",
      width: 100,
      defaultChecked: true,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="500">
          {params.row.vehicleNo || 'N/A'}
        </Typography>
      ),
    },
    {
      field: "deliveryDate",
      headerName: "Delivery Date",
      width: 120,
      defaultChecked: true,
      renderCell: (params) => (
        <Typography variant="body2">
          {formatDate(params.row.deliveryDate)}
        </Typography>
      ),
    },
    {
      field: "delvierySlotName",
      headerName: "Delivery Slot",
      width: 120,
      defaultChecked: true,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.row.delvierySlotName || 'N/A'}
        </Typography>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      width: 100,
      defaultChecked: true,
      renderCell: (params) => (
        <Chip 
          label={params.row.status} 
          size="small"
          color={getStatusColor(params.row.status)}
          sx={{ fontWeight: 'bold' }}
        />
      ),
    },
    {
      field: "createdBy",
      headerName: "Created By",
      width: 100,
      defaultChecked: true,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.row.createdBy}
        </Typography>
      ),
    },
    {
      field: "Action",
      headerName: "Actions",
      width: 130,
      defaultChecked: true,
      renderCell: (params) => (
        <Box display="flex" gap={1}>
          {/* Approval action - only show if status is not "approved" */}
          {params.row.status?.toLowerCase() !== 'approved' && (
            <IconButton 
              size="small" 
              onClick={() => handleApproveDeliverySheet(params.row.deliveryAssignId)}
              disabled={approving}
              sx={{ 
                color: 'success.main',
                '&:hover': { backgroundColor: 'success.light', color: 'white' }
              }}
            >
              {approving ? <CircularProgress size={16} /> : <CheckCircleIcon />}
            </IconButton>
          )}
          {/* Edit action - only show if user has edit permission */}
          {userPermissions.canEdit && (
            <IconButton 
              size="small" 
              onClick={() => handleEditDeliverySheet(params.row)}
              sx={{ 
                color: 'warning.main',
                '&:hover': { backgroundColor: 'warning.light', color: 'white' }
              }}
            >
              <FaEdit />
            </IconButton>
          )}
          {/* Delete action - only show if user has delete permission */}
          {userPermissions.canDelete && (
            <IconButton 
              size="small" 
              onClick={() => handleDeleteDeliverySheet(params.row.deliveryAssignId)}
              disabled={deleting}
              sx={{ 
                color: 'error.main',
                '&:hover': { backgroundColor: 'error.light', color: 'white' }
              }}
            >
              {deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
            </IconButton>
          )}
        </Box>
      ),
    },
  ];

  const handleOpenModal = (row: DeliverySheetData) => {
    localStorage.setItem('IsEdit', "False");
    setSelectedDeliverySheet(row);
    setOpenModal(true);
    localStorage.setItem('IsView', "True");
    localStorage.setItem('deliverySheetId', row.deliveryAssignId.toString());
    navigate("/deliverySheet/view")
  };

  const toggleFilter = (column: Column) => {
    setSearchInputValue("");
    if (openFilter === column.field) {
      setOpenFilter(null);
    } else {
      setOpenFilter(column.field);
    }
  };

  const handleInputFocus = (field: string) => {
    setOpenFilter(field);
  };

  const handleApplyFilterBtn = () => {
    // Filter implementation if needed
  };

  const linkFieldNav = (row: DeliverySheetData, column: string | undefined) => {
    if (column === "delivereyAssignNo") {
      return `/delivery-sheet/${row.deliveryAssignId}`;
    } else {
      return `#`;
    }
  };

  // Show permission loading
  // Keep references to some variables/functions to avoid TS "declared but never read" errors
  void selectedDeliverySheet;
  void openModal;
  void openCreateModal;
  void saving;
  void setDeliverySheetType;
  void showValidation;
  void handleSaveDeliverySheet;
  void calculateCutOffTime;

  if (userPermissions.loading) {
    return <Loader />;
  }

  // Check view permission - UPDATED to use userPermissions
  if (!userPermissions.canView) {
    return <ErrorPage error={{ code: '403', message: 'You do not have permission to view delivery sheets.' }} />;
  }

  if (tabView === '') {
    return (
      <ErrorPage
        error={{
          code: '403',
          message: "Whoops, access denied! Seems like you hit a dead end. Mysteries of the web, right?",
        }}
      />
    );
  }

  if (error) {
    return <ErrorPage error={error} />;
  }

  if (isLoading) {
    return <Loader />;
  }

  return (
    <>
      <Helmet>
        <title>{navUrl}</title>
      </Helmet>
      <Wrapper navUrl={navUrl} customStyle={{ height: "750px" }}>
        <WrapperSubHeader className="mainBreadcrumb">
          <div className="mr-auto">
            <SubHeaderTitle subTitle={capitalizeFirstChar(tabView)} />
          </div>
        </WrapperSubHeader>
        <WrapperContent>
          <Row>
            <WrapperCard isCollapsableAnalytics={false}>
              <CustomTabPanel>
                {/* Analytics section can be added here if needed */}
              </CustomTabPanel>
              
              <Box display="flex" justifyContent="flex-end" alignItems="center" mb={2} sx={{ px: 2, pt: '10px' }}>
                <Box display="flex" alignItems="center" gap={1}>
                  <TextField
                    size="small"
                    placeholder="Search delivery sheets..."
                    value={searchValue}
                    onChange={handleSearchChange}
                    InputProps={{
                      startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />,
                    }}
                    sx={{ width: 250 }}
                  />

                  {/* Create button - only show if user has insert permission */}
                  {userPermissions.canInsert && (
                    <CustomButton
                      onClick={() => {
                        resetForm();
                        navigate('/deliverySheet/create');
                        localStorage.setItem('IsEdit', "False");  
                        localStorage.setItem('IsView', "False");  
                      }}
                      btnName={"Create New"}
                      variant="contained"
                      color="primary"
                      size="small"
                    />
                  )}
                </Box>
              </Box>

              {/* Data Table */}
              {loadingData ? (
                <Box display="flex" justifyContent="center" py={4}>
                  <CircularProgress />
                </Box>
              ) : (
                <CustomDataTable
                  rows={rows}
                  columns={columnsDataTableDeliverySheet}
                  paginationInfo={pagination}
                  handlePageChange={handlePageChange}
                  isCheckboxShow={false}
                  FixedTable={true}
                  pageOption={pageOption}
                  handleChangePageOption={handleChangePageOption}
                  loading={loadingData}
                  isBranchFieldExist={false}
                  setCheckedValue={setCheckedValue}
                  checkedValue={checkedValue}
                  handleApplyFilterBtn={handleApplyFilterBtn}
                  toggleFilter={toggleFilter}
                  openFilter={openFilter}
                  setOpenFilter={setOpenFilter}
                  handleInputFocus={handleInputFocus}
                  branchLocationList={[]}
                  filterData={[]}
                  setFilterData={() => {}}
                  handleSearch={() => {}}
                  contactFilteredIds={[]}
                  setContactFilteredIds={() => {}}
                  filteringData={{}}
                  setSearchInputValue={setSearchInputValue}
                  searchInputValue={searchInputValue}
                  linkField={"delivereyAssignNo"}
                  linkFieldNav={linkFieldNav}
                />
              )}
            </WrapperCard>
          </Row>
        </WrapperContent>
      </Wrapper>

      {/* Snackbar for notifications - BOTTOM RIGHT */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ 
          vertical: snackbar.vertical, 
          horizontal: snackbar.horizontal 
        }}
        sx={{
          '& .MuiAlert-root': {
            fontSize: '14px',
            fontWeight: 500,
          }
        }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbar.severity}
          variant="filled"
          sx={{ 
            width: '100%',
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
            '&.MuiAlert-filledSuccess': {
              backgroundColor: '#4caf50',
            },
            '&.MuiAlert-filledError': {
              backgroundColor: '#f44336',
            },
            '&.MuiAlert-filledWarning': {
              backgroundColor: '#ff9800',
            },
            '&.MuiAlert-filledInfo': {
              backgroundColor: '#2196f3',
            }
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default DeliverySheet;